// =============================================================================
// EXPORT FLOW STATE — the ExportFlow overlay's pure decision logic, extracted
// so it's directly unit-testable without a component render harness (mirrors
// this app's own house pattern: driveExportState.ts / consentGateState.ts /
// printDocumentTitle.ts are all small, framework-free modules a component
// imports rather than inlining the logic in JSX).
//
// EXPORT-FLOW LANE (docs/design/export-workflow-2026-07-06.md) — the owner's
// verbatim ruling: "We don't need to see the resume templates until we want
// to export, which should be a different workflow." This module owns the
// open/close/selection/persist-trigger logic for that separated workflow;
// ExportFlow.tsx is the thin JSX shell around it (same division of labor as
// DriveSaveButton.tsx / driveExportState.ts).
//
// NO INVENTION: this module derives nothing about the user's content. It only
// sequences UI state (is the flow open, which destination is picked, does a
// selection change warrant a PATCH) and formats the filename preview from
// values the caller already has served (buildPrintDocumentTitle does the
// actual segment assembly — this file just decides WHEN to call it).
// =============================================================================

import type { PrintArtifact } from './printDocumentTitle';

export type ExportDestination = 'download' | 'drive';
export type ExportFormat = 'pdf' | 'docx';

/** Every honest phase the flow's own action (not the picker/selectors, which
 *  are always interactive once open) can be in. 'closed' is the flow fully
 *  dismissed/unmounted-in-effect (the trigger button's own resting state);
 *  'open' is the interactive picking phase; 'exporting' covers both the PDF/
 *  docx client-side render AND the Drive server round-trip (the caller's own
 *  DriveSaveButton-style network call decides which); 'done' is a completed
 *  download/save; 'error' surfaces a caught failure without losing the
 *  user's picks (closing and reopening the flow is the only way to reset,
 *  matching GapAddressPanel's own "the picks/turns stay visible through an
 *  error" posture). */
export type ExportFlowPhase = 'closed' | 'open' | 'exporting' | 'done' | 'error';

/** Whether the flow should currently render as open. A thin, testable named
 *  predicate rather than callers comparing `phase !== 'closed'` inline
 *  themselves at every call site. */
export function isExportFlowOpen(phase: ExportFlowPhase): boolean {
  return phase !== 'closed';
}

/** Whether the flow's own action controls (Export/Save buttons, the
 *  family/accent/destination pickers) should be disabled — true only while
 *  a real network/render call is in flight, mirroring DriveSaveButton's own
 *  "disabled only while checking/saving" posture (never disabled just
 *  because the user hasn't picked anything yet — every picker has a
 *  documented default). */
export function isExportFlowBusy(phase: ExportFlowPhase): boolean {
  return phase === 'exporting';
}

/**
 * Whether changing the family/accent inside the flow should fire the
 * existing preference PATCH (saveTemplatePreference) — true whenever the
 * NEW value actually differs from the CURRENTLY SAVED preference. A no-op
 * reselect (tapping the already-selected card again) or a value that
 * already matches the stored preference never fires a redundant network
 * call — mirrors useTemplatePreference's own optimistic-update shape, just
 * with the "is this actually a change" guard made explicit and testable
 * here rather than left to each call site to reimplement.
 */
export function shouldPersistSelection(nextValue: string, currentSavedValue: string): boolean {
  return nextValue !== currentSavedValue;
}

export interface ExportFilenameInput {
  displayName?: string | null;
  artifact: PrintArtifact;
  roleTitle?: string | null;
  company?: string | null;
  format: ExportFormat;
}

/** The extension (no leading dot) for a given export format — the ONE bit of
 *  format-specific naming this module owns; buildPrintDocumentTitle itself
 *  stays format-agnostic (it names artifacts, not files), so the flow adds
 *  the extension on top of that shared segment convention. */
export function extensionForFormat(format: ExportFormat): 'pdf' | 'docx' {
  return format;
}

/** True iff the Save-to-Drive destination should be offered at all in this
 *  render — mirrors the three existing mount sites' own gating (a
 *  DriveSaveButton only ever mounts once a real artifact id exists; the
 *  export flow's Drive destination option follows the exact same rule, so a
 *  résumé that hasn't been composed/built yet never offers a doomed Drive
 *  save). */
export function driveDestinationAvailable(artifactId: string | null | undefined): boolean {
  return typeof artifactId === 'string' && artifactId.length > 0;
}

/** Resolve the destination the flow should default to when it opens — Drive
 *  when a real artifact id is already in hand (the common "I already built
 *  this, now export it properly" path), download otherwise (an artifact
 *  that can't Drive-save yet still has an honest, always-available choice).
 *  Never a fabricated preference — this is a presentation default, not a
 *  persisted one (unlike family/accent, there is no destination column on
 *  the user; the user picks fresh, or takes this default, every time the
 *  flow opens). */
export function defaultDestination(artifactId: string | null | undefined): ExportDestination {
  return driveDestinationAvailable(artifactId) ? 'drive' : 'download';
}
